/**
 * TaskExplorerDialog — the "View all tasks" window.
 * Styled as a checklist table (Done | Deliverable | Day | Owner) grouped by phase,
 * matching the RTSO Delivery Checklist look.
 * - Done tasks strike through (emerald tint).
 * - Day cell renders "Day X" or "Day X to Y" (range) based on event_date anchor;
 *   inline editable for admin/team (saves to .../date endpoint).
 * - Owner tag per row.
 * - Progress bar at the top, phase filter dropdown + All/Upcoming/Done toggle.
 * - Team-side roles get half-hour time inputs + iteration counter inline.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  Check, Circle, Layers, Filter, ChevronDown, Video, FileCheck, Sparkles,
  Trophy, Flame,
} from "lucide-react";
import TaskTimeInput from "./TaskTimeInput";
import InlineDateCell from "./InlineDateCell";
import { useAuth } from "../lib/auth";

const OWNER_STYLE = {
  admin:            { label: "Admin",          cls: "bg-fuchsia-950/50 text-fuchsia-300 border-fuchsia-900/60" },
  account_manager:  { label: "Account Manager",cls: "bg-sky-950/50 text-sky-300 border-sky-900/60" },
  team:             { label: "Team",           cls: "bg-zinc-900 text-zinc-300 border-zinc-800" },
  creative:         { label: "Creative",       cls: "bg-amber-950/50 text-amber-300 border-amber-900/60" },
  strategy:         { label: "Strategy",       cls: "bg-emerald-950/50 text-emerald-300 border-emerald-900/60" },
  performance:      { label: "Performance",    cls: "bg-indigo-950/50 text-indigo-300 border-indigo-900/60" },
  "co-founder":     { label: "Co-founder",     cls: "bg-rose-950/50 text-rose-300 border-rose-900/60" },
  cofounder:        { label: "Co-founder",     cls: "bg-rose-950/50 text-rose-300 border-rose-900/60" },
  client:           { label: "Client",         cls: "bg-cyan-950/50 text-cyan-300 border-cyan-900/60" },
};
function OwnerChip({ owner }) {
  const cfg = OWNER_STYLE[(owner || "team").toLowerCase()] || OWNER_STYLE.team;
  return (
    <span className={`inline-flex items-center h-5 px-1.5 rounded text-[10px] uppercase tracking-wider font-mono border whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function daysFromStart(eng, iso) {
  if (!iso || !eng?.event_date) return null;
  const ev = new Date(eng.event_date + "T00:00:00");
  const d  = new Date(iso + "T00:00:00");
  const diff = Math.round((d - ev) / 86400000);     // days from event
  const startDay = diff + (eng.length_days || 30);  // Day 0 == start_date
  return startDay;
}
function dayLabel(eng, iso, isoEnd) {
  const a = daysFromStart(eng, iso);
  if (a == null) return "—";
  const b = daysFromStart(eng, isoEnd);
  if (b == null) return `Day ${a}`;
  return `Day ${a} to ${b}`;
}

export default function TaskExplorerDialog({ open, onOpenChange, engagement, onChanged }) {
  const { user } = useAuth();
  const isTeam = ["admin", "team", "account_manager"].includes(user?.role);
  const canEditDates = ["admin", "team", "account_manager"].includes(user?.role);
  const [eng, setEng] = useState(engagement);
  const [phaseId, setPhaseId] = useState("all");   // "all" | phase.id
  const [scope, setScope] = useState("all");       // "all" | "upcoming" | "done"

  useEffect(() => { setEng(engagement); }, [engagement]);

  const reload = async () => {
    if (!eng?.id) return;
    try {
      const r = await api.get(`/engagements/${eng.id}`);
      setEng(r.data);
      onChanged && onChanged();
    } catch { /* silent */ }
  };

  const phases = useMemo(() => [...(eng?.phases || [])].sort((a, b) => (a.order || 0) - (b.order || 0)), [eng]);
  const items = useMemo(() => {
    const d = (eng?.deliverables || []).map((x) => ({ ...x, __kind: "deliverable" }));
    const m = (eng?.meetings || []).map((x) => ({ ...x, __kind: "meeting" }));
    return [...d, ...m].filter((x) => !x.dismissed);
  }, [eng]);

  const filtered = useMemo(() => {
    let out = items;
    if (phaseId !== "all") out = out.filter((x) => x.phase_id === phaseId);
    if (scope === "upcoming") out = out.filter((x) => !x.done);
    if (scope === "done") out = out.filter((x) => x.done);
    return out.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
  }, [items, phaseId, scope]);

  const groups = useMemo(() => {
    if (phaseId !== "all") {
      const p = phases.find((p) => p.id === phaseId);
      return [{ phase: p || { name: "Ungrouped", id: "none" }, items: filtered }];
    }
    const map = new Map();
    for (const it of filtered) {
      const key = it.phase_id || "none";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    const orderIx = new Map(phases.map((p, i) => [p.id, i]));
    return Array.from(map.entries())
      .map(([pid, its]) => ({ phase: phases.find((p) => p.id === pid) || { id: pid, name: "Other", order: 999 }, items: its }))
      .sort((a, b) => (orderIx.get(a.phase.id) ?? 999) - (orderIx.get(b.phase.id) ?? 999));
  }, [filtered, phaseId, phases]);

  const totals = useMemo(() => {
    const t = items.length;
    const d = items.filter((x) => x.done).length;
    return { total: t, done: d, pct: t ? Math.round(100 * d / t) : 0 };
  }, [items]);

  const tick = async (it, done) => {
    if (!isTeam) return;
    const path = it.__kind === "meeting" ? "meetings" : "deliverables";
    try {
      await api.post(`/engagements/${eng.id}/${path}/${it.id}/tick`, { done });
      reload();
    } catch { toast.error("Tick failed"); }
  };

  if (!eng) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-4xl max-h-[86vh] flex flex-col p-0"
        data-testid="task-explorer">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-900">
          <DialogTitle className="text-zinc-100 text-[15px] font-display font-semibold flex items-center gap-2">
            <Layers className="w-4 h-4 text-zinc-400" /> Delivery checklist
            <span className="text-zinc-500 text-[12px] font-normal">· {eng.name}</span>
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-[11px]">
            Deliverables and meetings grouped by phase. Click a date to edit it; dates auto-fill from the template.
          </DialogDescription>

          {/* Gamified progress bar */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 relative h-2.5 rounded-full bg-zinc-900 overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 via-emerald-400 to-sky-400 transition-[width] duration-700 ease-out"
                style={{ width: `${totals.pct}%` }} data-testid="task-explorer-progress" />
              {totals.pct >= 100 && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white/80" />
                </div>
              )}
            </div>
            <div className="text-[11px] font-mono text-zinc-400 tabular-nums w-24 text-right">
              {totals.done}/{totals.total} · <span className="text-zinc-200">{totals.pct}%</span>
            </div>
            {totals.pct >= 100 && (
              <div className="flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-950/60 border border-emerald-800 rounded px-2 py-0.5 font-mono uppercase tracking-wider">
                <Trophy className="w-2.5 h-2.5" /> complete
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
              <Filter className="w-3 h-3" /> Phase
            </div>
            <div className="relative">
              <select value={phaseId} onChange={(e) => setPhaseId(e.target.value)}
                className="appearance-none bg-[#111] border border-zinc-800 rounded pl-2 pr-6 h-7 text-[11px] text-zinc-200 focus:outline-none focus:border-zinc-600"
                data-testid="phase-filter">
                <option value="all">All phases ({phases.length})</option>
                {phases.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
              <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            </div>
            <div className="flex bg-[#111] border border-zinc-800 rounded p-0.5" data-testid="scope-filter">
              {[{ v: "all", l: "All" }, { v: "upcoming", l: "Upcoming" }, { v: "done", l: "Done" }].map((s) => (
                <button key={s.v} type="button" onClick={() => setScope(s.v)}
                  data-testid={`scope-${s.v}`}
                  className={`px-2.5 h-6 rounded text-[10px] uppercase tracking-wider font-mono transition-colors ${
                    scope === s.v ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-zinc-200"
                  }`}>
                  {s.l}
                </button>
              ))}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" data-testid="task-explorer-body">
          {groups.length === 0 && (
            <div className="text-center text-zinc-500 text-[12px] py-10">No tasks match this filter.</div>
          )}
          {groups.map((g) => {
            const done = g.items.filter((x) => x.done).length;
            const total = g.items.length;
            const pct = total ? Math.round(100 * done / total) : 0;
            return (
              <div key={g.phase.id} data-testid={`phase-group-${g.phase.id}`}>
                {/* Phase header */}
                <div className="flex items-baseline justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-1 h-4 rounded bg-gradient-to-b from-sky-400 to-emerald-500" />
                    <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-200 font-mono">
                      {g.phase.name || "Ungrouped"}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-zinc-500 tabular-nums">{done}/{total} · {pct}%</span>
                </div>
                <div className="h-1 rounded-full bg-zinc-900 overflow-hidden mb-2">
                  <div className="h-full bg-zinc-500/80 transition-[width] duration-500" style={{ width: `${pct}%` }} />
                </div>

                {/* Checklist table */}
                <div className="border border-zinc-900 rounded overflow-hidden">
                  <div className="grid grid-cols-[32px_1fr_170px_140px] bg-zinc-950/80 text-[10px] font-mono uppercase tracking-wider text-zinc-500 px-2 py-1.5 border-b border-zinc-900">
                    <div>Done</div>
                    <div>Deliverable</div>
                    <div>Day</div>
                    <div>Owner</div>
                  </div>
                  <ul className="divide-y divide-zinc-900/70">
                    {g.items.map((it) => (
                      <li key={`${it.__kind}-${it.id}`}
                        data-testid={`explorer-task-${it.id}`}
                        className={`grid grid-cols-[32px_1fr_170px_140px] items-start px-2 py-2 text-[12.5px] transition-colors ${
                          it.done ? "bg-emerald-950/15" : "hover:bg-zinc-950/40"
                        }`}>
                        <div className="pt-0.5">
                          <button
                            type="button"
                            disabled={!isTeam}
                            onClick={() => tick(it, !it.done)}
                            data-testid={`explorer-tick-${it.id}`}
                            className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                              it.done
                                ? "bg-emerald-500 border-emerald-500 text-black"
                                : "border-zinc-700 text-transparent hover:border-emerald-400 hover:text-emerald-400"
                            } ${!isTeam ? "cursor-default" : "cursor-pointer"}`}>
                            {it.done ? <Check className="w-2.5 h-2.5" strokeWidth={4} /> : <Circle className="w-2 h-2 opacity-0" />}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                          {it.__kind === "meeting"
                            ? <Video className="w-3 h-3 text-sky-400 shrink-0" />
                            : <FileCheck className="w-3 h-3 text-zinc-500 shrink-0" />}
                          <span className={`truncate ${it.done ? "line-through text-zinc-500" : "text-zinc-100"}`}
                            data-testid={`explorer-title-${it.id}`}>
                            {it.title}
                          </span>
                          {(it.iterations || 0) > 0 && (
                            <span className="text-[9px] font-mono text-amber-300 bg-amber-950/40 border border-amber-900/50 rounded px-1.5 py-0.5 flex items-center gap-1 shrink-0"
                              data-testid={`explorer-iter-badge-${it.id}`}>
                              <Flame className="w-2 h-2" /> v{it.iterations}
                            </span>
                          )}
                        </div>
                        <div className="pt-0.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-mono text-zinc-500 w-16 shrink-0 uppercase tracking-wider">
                              {dayLabel(eng, it.due_date, it.due_date_end)}
                            </span>
                            <InlineDateCell
                              engagementId={eng.id}
                              kind={it.__kind}
                              itemId={it.id}
                              startDate={it.due_date}
                              endDate={it.due_date_end}
                              canEdit={canEditDates}
                              onSaved={reload} />
                          </div>
                        </div>
                        <div className="pt-0.5">
                          <OwnerChip owner={it.owner_role} />
                        </div>
                        {isTeam && (
                          <div className="col-span-4 pl-7 pt-1.5">
                            <TaskTimeInput
                              engagementId={eng.id}
                              kind={it.__kind}
                              itemId={it.id}
                              initial={{
                                hours_am: it.hours_am, hours_designer: it.hours_designer,
                                hours_editor: it.hours_editor, iterations: it.iterations,
                              }}
                              onSaved={reload}
                              compact />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
